import type { ErrorEvent, EventHint } from '@sentry/react';
import { ApiError } from './ApiError';

// 401 is handled by the silent-refresh/redirect flow. Other 4xx (403, 404, ...) stay as `warning`
// events — same policy as reportError — so they remain visible without paging on level:error.
const IGNORED_STATUSES = new Set([401]);

// Every ApiError is constructed at the same interceptor frame, so Sentry's default
// stack-based grouping would merge unrelated endpoints into one issue. Runs as
// `beforeSend`, so it applies to every capture site (QueryProvider and the
// components that call Sentry.captureException themselves).
export const apiErrorBeforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  const error = hint.originalException;
  if (!(error instanceof ApiError)) return event;

  if (error.status !== null && IGNORED_STATUSES.has(error.status)) return null;

  const method = error.method ?? 'UNKNOWN';
  const endpoint = error.endpoint ?? 'unknown';
  const status = error.status === null ? 'network' : String(error.status);

  return {
    ...event,
    level: error.status !== null && error.status < 500 ? 'warning' : 'error',
    fingerprint: ['api-error', method, endpoint, status],
    tags: { ...event.tags, 'api.method': method, 'api.endpoint': endpoint, 'api.status': status },
  };
};
